import { useBreakoutUser } from 'hooks/profile'
import type { Dispatch, SetStateAction } from 'react'
import classNames from 'classnames'
import { ChevronRight } from 'components/icons/ChevronRight'
import { ProfilePages } from '../../types/ProfilePages'
import { BreakoutSelect } from 'components/design-system/BreakoutSelect'
import { BreakoutUserAvatar } from 'components/breakout/BreakoutUserAvatar'
import { SuperAdminPrivilege } from '@breakoutlearning/firebase-repository/types'
import { useTranslationTyped } from 'i18n/i18n'
import { observer } from 'mobx-react-lite'
import { AdminOnlyRing } from 'components/AdminOnlyRing'
import { Tabs } from 'radix-ui'
import { isProdOrBeta } from 'config/environment'

export const ProfileNavigation = observer(function ProfileNavigation({
  selectedPage,
  setSelectedPage,
}: {
  selectedPage: ProfilePages
  setSelectedPage: Dispatch<SetStateAction<ProfilePages>>
}) {
  const breakoutUser = useBreakoutUser()
  const { tt } = useTranslationTyped()
  const allowLtiSettings = breakoutUser.isAdmin && !isProdOrBeta()
  // Sections read defaults from the instructor's profile, and the save
  // endpoint allows only instructors (BL-4757), so the page is pointless for
  // anyone else.
  const showScoreMappingDefaults = breakoutUser.isInstructor

  return (
    <>
      <div className="block md:hidden">
        <BreakoutSelect
          kind="secondary"
          inputClassName="mb-5"
          value={selectedPage}
          onChange={(value) => setSelectedPage(value)}
          options={
            [
              {
                value: ProfilePages.details,
                label: tt.profile.personal_details(),
              },
              showScoreMappingDefaults && {
                value: ProfilePages.scoreMappingDefaults,
                label: tt.profile.score_mapping_defaults(),
              },
              {
                value: ProfilePages.transactions,
                label: tt.profile.transactions(),
              },
              { value: ProfilePages.avTest, label: tt.profile.av_test() },
              {
                value: ProfilePages.podcast,
                label: tt.profile.your_podcasts(),
              },
              breakoutUser.isInstructor && {
                value: ProfilePages.promotions,
                label: tt.profile.promotions(),
              },
              breakoutUser.isInternal && {
                value: ProfilePages.settings,
                label: tt.profile.settings(),
              },
              allowLtiSettings && {
                value: ProfilePages.ltiSettings,
                label: tt.profile.lti_settings(),
              },
            ].filter(Boolean) as { value: ProfilePages; label: string }[]
          }
        />
      </div>
      <div className="hidden h-full w-full max-w-[400px] rounded-3xl bg-surface p-5 md:block">
        <div className="h-full overflow-y-auto p-5">
          <div className="flex items-center">
            <div className="mr-2 shrink-0">
              <BreakoutUserAvatar radius={25} user={breakoutUser.publicUser} />
            </div>
            <div>
              <h2
                data-testid="profile-user-name"
                className="text-title-large text-core-on-secondary"
              >
                {breakoutUser.firstName} {breakoutUser.lastName}
              </h2>
              <div
                data-testid="profile-user-email"
                className="text-body-medium text-on-surface-var"
              >
                {breakoutUser.emailAddress}
              </div>
            </div>
          </div>
          <Tabs.List className="mt-5 space-y-2">
            <NavigationOption
              label={tt.profile.personal_details()}
              isSelected={selectedPage === ProfilePages.details}
              value={ProfilePages.details}
              dataTestId="profile-personal-details"
            />
            {showScoreMappingDefaults && (
              <NavigationOption
                label={tt.profile.score_mapping_defaults()}
                isSelected={selectedPage === ProfilePages.scoreMappingDefaults}
                value={ProfilePages.scoreMappingDefaults}
                dataTestId="profile-score-mapping-defaults"
              />
            )}
            <NavigationOption
              label={tt.profile.transactions()}
              isSelected={selectedPage === ProfilePages.transactions}
              value={ProfilePages.transactions}
              dataTestId="profile-transactions"
            />
            <NavigationOption
              label={tt.profile.av_test()}
              isSelected={selectedPage === ProfilePages.avTest}
              value={ProfilePages.avTest}
              dataTestId="profile-av-test"
            />
            <NavigationOption
              label={tt.profile.your_podcasts()}
              isSelected={selectedPage === ProfilePages.podcast}
              value={ProfilePages.podcast}
              dataTestId="profile-podcasts"
            />
            {breakoutUser.isInstructor && (
              <NavigationOption
                label={tt.profile.promotions()}
                isSelected={selectedPage === ProfilePages.promotions}
                value={ProfilePages.promotions}
                dataTestId="profile-promotions"
              />
            )}
            <AdminOnlyRing
              label={tt.roles.internal_use_only()}
              ringClassName="!mt-4"
              onlyShowIf={breakoutUser.isInternal}
            >
              <NavigationOption
                label={tt.profile.settings()}
                isSelected={selectedPage === ProfilePages.settings}
                value={ProfilePages.settings}
                dataTestId="profile-settings"
              />
            </AdminOnlyRing>
            <AdminOnlyRing
              label={tt.roles.admin_only()}
              ringClassName="!mt-4"
              onlyShowIf={allowLtiSettings}
            >
              <NavigationOption
                label={tt.profile.lti_settings()}
                isSelected={selectedPage === ProfilePages.ltiSettings}
                value={ProfilePages.ltiSettings}
                dataTestId="profile-lti-settings"
              />
            </AdminOnlyRing>
            <AdminOnlyRing
              label={tt.roles.special_admins_only()}
              ringClassName="!mt-4"
              onlyShowIf={
                breakoutUser.isAdmin &&
                breakoutUser.superAdmin.isLoaded &&
                breakoutUser.isSuperAdmin &&
                breakoutUser.superAdmin.hasPrivilege(
                  SuperAdminPrivilege.canViewFeatureFlags
                )
              }
            >
              <NavigationOption
                label={tt.profile.view_feature_flags()}
                isSelected={selectedPage === ProfilePages.viewFeatureFlags}
                value={ProfilePages.viewFeatureFlags}
                dataTestId="profile-view-feature-flags"
              />
            </AdminOnlyRing>
          </Tabs.List>
          <a
            className="group mt-2 flex h-[52px] w-full cursor-pointer items-center justify-between rounded-2xl bg-core-tertiary px-4 text-label-medium text-core-on-tertiary hover:ring-1 hover:ring-outline-light"
            href="https://www.breakoutlearning.com/legal/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="profile-privacy-policy"
          >
            {tt.profile.privacy_policy()}
          </a>
        </div>
      </div>
    </>
  )
})

const NavigationOption = ({
  label,
  isSelected,
  dataTestId,
  value,
}: {
  label: string
  isSelected: boolean
  dataTestId?: string
  value: ProfilePages
}) => {
  return (
    <Tabs.Trigger
      data-testid={dataTestId}
      className={classNames(
        'group flex h-[52px] w-full cursor-pointer items-center justify-between rounded-2xl bg-core-tertiary px-4 text-label-medium text-core-on-tertiary hover:ring-1 hover:ring-outline-light',
        {
          'ring-1 ring-outline-light': isSelected,
        }
      )}
      value={value}
    >
      {label}
      {isSelected && (
        <div>
          <ChevronRight size={15} />
        </div>
      )}
    </Tabs.Trigger>
  )
}
